import { z } from "zod";
import { airSequenceSchema, airTargetSchema } from "./air.js";
import { gridStateSchema } from "./grid.js";
import { emptyLineup, lineupStateSchema } from "./lineup.js";
import { previewSchema } from "./preview.js";
import { matchPhaseSchema, statsFieldsSchema } from "./state.js";

// Team snapshot the overlay needs to render badges/colors. Synced from PFL.
export const teamInfoSchema = z.object({
  id: z.string(),
  pflId: z.string(),
  name: z.string(),
  shortName: z.string(),
  /** PFL short code (e.g. "NSF") — the scoreboard bar renders this. */
  code: z.string().nullable().default(null),
  logoUrl: z.string().nullable(),
  colorPrimary: z.string().nullable(),
  colorSecondary: z.string().nullable(),
});
export type TeamInfo = z.infer<typeof teamInfoSchema>;

export const themeSchema = z.enum(["uzsl", "proliga", "cup", "premium"]);
export type Theme = z.infer<typeof themeSchema>;

// VAR review state. The overlay-var graphic reads this; control sends
// var.show / var.decide / var.hide to drive it.
export const varSubjectSchema = z.enum(["goal", "penalty", "red_card", "ident"]);
export type VarSubject = z.infer<typeof varSubjectSchema>;

export const varPhaseSchema = z.enum(["checking", "decided"]);
export type VarPhase = z.infer<typeof varPhaseSchema>;

// VAR decisions — SKORE var_json: yes/no + the yellow variant in a red-card check.
// yellow_cancel is not in SKORE — VAR cancelling a yellow already given (ours).
export const varDecisionSchema = z.enum(["yes", "no", "yellow", "yellow_no", "yellow_cancel"]);
export type VarDecision = z.infer<typeof varDecisionSchema>;

// Per-player card state — derived from the event log (server
// recomputeCards). The panel reads it to detect a 2nd yellow.
export const playerCardStateSchema = z.object({
  yellows: z.number().int().nonnegative().default(0),
  sentOff: z.boolean().default(false),
});
export type PlayerCardState = z.infer<typeof playerCardStateSchema>;

export const varOverlaySchema = z.object({
  visible: z.boolean().default(false),
  subject: varSubjectSchema.nullable().default(null),
  phase: varPhaseSchema.nullable().default(null),
  decision: varDecisionSchema.nullable().default(null),
  shownAt: z.number().int().nonnegative().nullable().default(null),
  subjectPlayerId: z.string().nullable().default(null),
  /** The logged event under review (goal / card) — the decision corrects exactly it. */
  subjectEventId: z.string().nullable().default(null),
});
export type VarOverlayState = z.infer<typeof varOverlaySchema>;

// Crew / match officials. Each entry is a plain person name; the names_* and
// coach_id_* overlays read these via overlay.show data. Operator-entered in
// the Control Room Setup drawer (PFL pre-fill is a later addition).
export const personNameSchema = z.object({
  firstName: z.string().default(""),
  lastName: z.string().default(""),
  photoUrl: z.string().nullish(),
});
export type PersonName = z.infer<typeof personNameSchema>;

export const crewSchema = z.object({
  referee: personNameSchema.nullable().default(null),
  refereeHelper1: personNameSchema.nullable().default(null),
  refereeHelper2: personNameSchema.nullable().default(null),
  refereeHelper3: personNameSchema.nullable().default(null),
  var: personNameSchema.nullable().default(null),
  avar: personNameSchema.nullable().default(null),
  commentator1: personNameSchema.nullable().default(null),
  commentator2: personNameSchema.nullable().default(null),
  coachHome: personNameSchema.nullable().default(null),
  coachAway: personNameSchema.nullable().default(null),
});
export type Crew = z.infer<typeof crewSchema>;

// Sponsor bumper text (little_sponsor / little_sponsor_2 overlays).
export const sponsorInfoSchema = z.object({
  name: z.string().default(""),
  tagline: z.string().nullable().default(null),
  logoUrl: z.string().nullable().default(null),
});
export type SponsorInfo = z.infer<typeof sponsorInfoSchema>;

// Free-form lower-third (little_names_universal) — e.g. a commissioner.
export const universalNameSchema = z.object({
  firstName: z.string().default(""),
  lastName: z.string().default(""),
  label: z.string().default(""),
});
export type UniversalName = z.infer<typeof universalNameSchema>;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/** Per-match premium theme brand; null = the theme's own value. */
export const premiumBrandSchema = z.object({
  primary: hexColor.nullable().default(null),
  accent: hexColor.nullable().default(null),
  sponsorLogoUrl: z.string().nullable().default(null),
});
export type PremiumBrand = z.infer<typeof premiumBrandSchema>;

export const matchMetadataSchema = z.object({
  stadium: z.string().nullable().default(null),
  city: z.string().nullable().default(null),
  sponsor1: sponsorInfoSchema.nullable().default(null),
  sponsor2: sponsorInfoSchema.nullable().default(null),
  universal: universalNameSchema.nullable().default(null),
  /** Per-match league logo (uploaded via /api/league-logos) — replaces the
   *  theme's competition logo on the scoreboard and BIG. null = theme default. */
  leagueLogoUrl: z.string().nullable().default(null),
  premium: premiumBrandSchema.default({}),
});
export type MatchMetadata = z.infer<typeof matchMetadataSchema>;

// Pitch conditions — PFL doesn't provide these, so the operator enters them.
export const weatherSchema = z.object({
  temperature: z.number().nullable().default(null),
  humidity: z.number().min(0).max(100).nullable().default(null),
  wind: z.number().nullable().default(null),
  condition: z.string().nullable().default(null),
});
export type Weather = z.infer<typeof weatherSchema>;

/** `weather.condition` values with an icon on air (the icon before the
 *  clock). Anything else — including null and old free-text values — shows
 *  the moon, the original SKORE icon. */
export const WEATHER_CONDITIONS = [
  { id: "moon", label: "Oy" },
  { id: "sun", label: "Quyosh" },
  { id: "cloud", label: "Bulut" },
  { id: "rain", label: "Yomg'ir" },
  { id: "snow", label: "Qor" },
  { id: "storm", label: "Chaqmoq" },
] as const;
export type WeatherConditionId = (typeof WEATHER_CONDITIONS)[number]["id"];

export function weatherConditionId(value: string | null | undefined): WeatherConditionId {
  return WEATHER_CONDITIONS.find((c) => c.id === value)?.id ?? "moon";
}

// Penalty shootout — one entry per kick, in order. "goal"/"miss"/"pending"
// map to SKORE's g/d/0. `roundnum` is NOT stored: the current kick is the
// first "pending" (the graphic derives it). Default: 5 pending each side;
// sudden death extends the arrays (control appends "pending").
export const penaltyKickSchema = z.enum(["goal", "miss", "pending"]);
export type PenaltyKick = z.infer<typeof penaltyKickSchema>;

const fivePending = (): PenaltyKick[] => ["pending", "pending", "pending", "pending", "pending"];

export const penaltyShootoutSchema = z.object({
  home: z.array(penaltyKickSchema).min(1).max(15).default(fivePending),
  away: z.array(penaltyKickSchema).min(1).max(15).default(fivePending),
});
export type PenaltyShootout = z.infer<typeof penaltyShootoutSchema>;

// Player Compare (dostats) — a two-player comparison. The extension computes it,
// the panel sends the selection, the graphic renders `payload` unchanged.
export const playerCompareSceneSchema = z.enum(["pre", "ht"]);
export type PlayerCompareScene = z.infer<typeof playerCompareSceneSchema>;
export const playerCompareSourceSchema = z.enum(["season", "last"]);
export type PlayerCompareSource = z.infer<typeof playerCompareSourceSchema>;

export const playerCompareRowSchema = z.object({
  label: z.string(),
  /** Unit in parentheses, e.g. "(km)" — the graphic renders it in a smaller type size. */
  unit: z.string().optional(),
  /** Already formatted text; a missing metric is «—». */
  home: z.string(),
  away: z.string(),
});
export type PlayerCompareRow = z.infer<typeof playerCompareRowSchema>;

export const playerCompareSideSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  photoUrl: z.string().nullable(),
  position: z.string().nullable(),
});
export type PlayerCompareSide = z.infer<typeof playerCompareSideSchema>;

export const playerComparePayloadSchema = z.object({
  scene: playerCompareSceneSchema,
  title: z.string(),
  home: playerCompareSideSchema,
  away: playerCompareSideSchema,
  rows: z.array(playerCompareRowSchema),
  fetchedAt: z.number().int().nonnegative(),
});
export type PlayerComparePayload = z.infer<typeof playerComparePayloadSchema>;

/** 1st-half snapshot — local copy of the player match agg: dostats player_id → stat → value. */
export const htSnapshotSchema = z.object({
  takenAt: z.number().int().nonnegative(),
  matchId: z.number().int(),
  stats: z.record(z.string(), z.record(z.string(), z.number())),
});
export type HtSnapshot = z.infer<typeof htSnapshotSchema>;

export const playerCompareSchema = z.object({
  scene: playerCompareSceneSchema.default("pre"),
  source: playerCompareSourceSchema.default("season"),
  home: z.string().nullable().default(null),
  away: z.string().nullable().default(null),
  status: z.enum(["idle", "loading", "ready", "error"]).default("idle"),
  error: z.string().nullable().default(null),
  warning: z.string().nullable().default(null),
  htSnapshot: htSnapshotSchema.nullable().default(null),
  payload: playerComparePayloadSchema.nullable().default(null),
});
export type PlayerCompareState = z.infer<typeof playerCompareSchema>;

export function emptyPlayerCompare(): PlayerCompareState {
  return playerCompareSchema.parse({});
}

// Player Stat (dostats) — a single-player lower-third titre. The extension
// computes it, the panel sends the selection, the graphic renders `payload` unchanged.
export const playerStatSourceSchema = z.enum(["season", "match"]);
export type PlayerStatSource = z.infer<typeof playerStatSourceSchema>;

export const playerStatItemSchema = z.object({
  /** Icon key — the graphic maps it to an image in CSS. */
  icon: z.string(),
  /** Already formatted text, e.g. "22 gol". */
  text: z.string(),
});
export type PlayerStatItem = z.infer<typeof playerStatItemSchema>;

export const playerStatPayloadSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  logoUrl: z.string().nullable(),
  /** Bottom label: "Joriy mavsumda" / "Shu o‘yinda". */
  pill: z.string(),
  items: z.array(playerStatItemSchema),
  fetchedAt: z.number().int().nonnegative(),
});
export type PlayerStatPayload = z.infer<typeof playerStatPayloadSchema>;

export const playerStatSchema = z.object({
  playerId: z.string().nullable().default(null),
  source: playerStatSourceSchema.default("season"),
  status: z.enum(["idle", "loading", "ready", "error"]).default("idle"),
  error: z.string().nullable().default(null),
  payload: playerStatPayloadSchema.nullable().default(null),
});
export type PlayerStatState = z.infer<typeof playerStatSchema>;

export function emptyPlayerStat(): PlayerStatState {
  return playerStatSchema.parse({});
}

export const projectStateSchema = z.object({
  slug: z.string(),
  matchId: z.string(),
  name: z.string(),
  theme: themeSchema.default("uzsl"),
  /** Bumped when the project is rebuilt from the DB (PFL resync): panels
   *  refetch the catalog (roster, flags) instead of keeping a stale copy. */
  catalogRev: z.number().nullish(),

  match: z.object({
    homeScore: z.number().int().nonnegative().default(0),
    awayScore: z.number().int().nonnegative().default(0),
    minute: z.number().int().min(0).max(130).default(0),
    second: z.number().int().min(0).max(59).default(0),
    phase: matchPhaseSchema.default("pre_match"),
    additionalTime: z.number().int().nonnegative().default(0),
    /** Whether the "+X" strip under the clock is ON AIR. The referee may raise
     *  the board at 45:10 rather than 45:00 — so the strip is not tied to the clock;
     *  the operator controls it with the "Extra time" tile or the "Strip efirda"
     *  checkbox. A phase change (clock.setPhase) clears it on its own. */
    extraTimeOnAir: z.boolean().default(false),
    running: z.boolean().default(false),
    /** Wall-clock anchor of the clock: the precise elapsed ms (`clockBaseMs`)
     *  as of `clockStartedAt` (epoch ms, null while stopped). The ticker
     *  derives minute/second from Date.now(), so a late tick never loses time
     *  and a pause keeps the second's fraction. Absent on pre-anchor state. */
    clockStartedAt: z.number().nullish(),
    clockBaseMs: z.number().nonnegative().nullish(),
    homeStats: statsFieldsSchema,
    awayStats: statsFieldsSchema,
    /** The last values dostats sent, per side. homeStats/awayStats show them
     *  unless the operator corrected a field — those keys are listed in
     *  `statsManual` and the API no longer overwrites them until reset. */
    statsApi: z
      .object({ home: statsFieldsSchema.partial(), away: statsFieldsSchema.partial() })
      .nullish(),
    statsManual: z.object({ home: z.array(z.string()), away: z.array(z.string()) }).nullish(),
    /** When the operator last pulled stats from dostats (epoch ms). */
    statsFetchedAt: z.number().nullish(),
    playerCards: z.record(z.string(), playerCardStateSchema).default({}),
    updatedAt: z.number().int().nonnegative(),
  }),

  // What the scoreboard overlay is currently rendering. `mode === null`
  // means nothing is on-screen for this project right now.
  overlay: z.object({
    mode: z.string().nullable().default(null),
    data: z.unknown().optional(),
    shownAt: z.number().int().nonnegative().nullable().default(null),
    /** Where turning a fullscreen board off goes back to; null = empty air. */
    returnTo: airTargetSchema.nullish(),
    /** Was the scoreboard visible under the titre on air. */
    scoreboardUnder: z.boolean().nullish(),
    /** A server-run banner chain (goal, card, swap…), see air.ts. */
    sequence: airSequenceSchema.nullish(),
  }),

  // VAR review overlay (scene in the merged overlay page).
  varOverlay: varOverlaySchema.default({
    visible: false,
    subject: null,
    phase: null,
    decision: null,
    shownAt: null,
    subjectPlayerId: null,
    subjectEventId: null,
  }),

  // Formation / lineup overlay (scene in the merged overlay page).
  lineup: lineupStateSchema.default(emptyLineup()),

  // Tactical placement (GRID overlay) — the SKORE plgrid model.
  grid: gridStateSchema.default({ home: [], away: [] }),

  teams: z.object({
    home: teamInfoSchema.nullable().default(null),
    away: teamInfoSchema.nullable().default(null),
  }),

  // SKORE-style crew / metadata / weather. All operator-editable; consumed by
  // the names_*, coach_id_*, sponsor and (future) info overlays.
  crew: crewSchema.default({}),
  metadata: matchMetadataSchema.default({}),
  weather: weatherSchema.default({}),

  // SKORE-style "Auto sponsor": when true the score graphic appends the
  // sponsor strip (metadata.sponsor1/2) under every non-sponsor info panel.
  autoSponsor: z.boolean().default(false),

  // Efir view cue (preview → take). Absent = nothing cued.
  preview: previewSchema.nullish(),

  // Penalty shootout state (little_pen overlay + PenaltyPanel).
  penalties: penaltyShootoutSchema.default({}),

  // Player Compare (dostats) — the selection + the computed payload.
  playerCompare: playerCompareSchema.default({}),

  // Player Stat (dostats) — a single-player titre.
  playerStat: playerStatSchema.default({}),
});
export type ProjectState = z.infer<typeof projectStateSchema>;

// Top-level replicant: all projects keyed by slug.
export const projectsReplicantSchema = z.record(z.string(), projectStateSchema);
export type ProjectsReplicant = z.infer<typeof projectsReplicantSchema>;

export const REPLICANT_NAMES = {
  projects: "projects",
  activeSlug: "activeSlug",
} as const;

// NodeCG message name → command type. The control panel emits
// `nodecg.sendMessage('cmd', { slug, type, payload })`. The extension
// dispatches on `type` using the discriminated union from commands.ts.
export const COMMAND_MESSAGE_NAME = "cmd" as const;
